<script lang="ts">
  import {DropdownButton} from '@/components/button';
  import {getDownloadOptionsContext} from '@/contexts/download';
  import {isMobile, user} from '@/store';
  import {Devotional, Video} from '@/types/project';
  import {download} from '@/utils/common';
  import {httpClient} from '@/utils/http-client';
  import {onMount} from 'svelte';
  import {parseDevotionals} from './Devotionals';

  export let selectedVideo: Video | null;

  const type = 'Devotional';
  const downloadOptions = getDownloadOptionsContext();
  let devotionals: Devotional[] = [];
  let warningMessage: string = '';

  $: isLoading = true;

  const handleDownload = async (extension: 'txt' | 'html' | 'pdf' = 'txt') => {
    const filename = `[${type}] ${selectedVideo?.title}`;
    const text = parseDevotionals(devotionals, extension);

    await download(filename, text, type, extension);
  };

  async function regenerate() {
    if (!selectedVideo) throw 'selectedVideo is null';
    isLoading = true;

    await httpClient
      .get(`/sermon-summary/${selectedVideo.id}/devotionals/regenerate`)
      .then(response => {
        devotionals = response.data.result.devotional;
      })
      .catch(error => {
        if (selectedVideo?.metadata && selectedVideo?.metadata?.duration < 300) {
          warningMessage =
            "The video you've uploaded is too short for our content generation system to work effectively. Please upload a video that's at least 5 minutes long to get AI-powered content.";
        } else {
          warningMessage = 'Failed to Generate Devotionals.';
        }
        console.log(error);
      })
      .finally(() => {
        isLoading = false;
      });
  }

  onMount(async () => {
    if (!selectedVideo) throw 'selectedVideo is null';

    await httpClient
      .get(`/sermon-summary/${selectedVideo.id}/devotionals`)
      .then(response => {
        devotionals = response.data.result.devotional;
        if (devotionals.length === 0) {
          if (selectedVideo?.metadata && selectedVideo?.metadata?.duration < 300) {
            warningMessage =
              "The video you've uploaded is too short for our content generation system to work effectively. Please upload a video that's at least 5 minutes long to get AI-powered content.";
          } else {
            warningMessage = 'Failed to Generate Devotionals.';
          }
        }
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        isLoading = false;
      });
  });
</script>

<div class="h-full w-full">
  <div class="flex h-16 w-full items-center justify-between border-b border-b-gray-200 px-4 dark:border-b-zinc-600">
    {#if !$isMobile}
      <h2 class="text-md w-full font-bold text-gray-900">5 Day Devotional</h2>
    {/if}
    <div class="flex w-full justify-center space-x-2 py-2 lg:justify-end lg:py-0">
      {#if $user.isImpersonated || $user.isAdmin}
        <button
          type="button"
          on:click={() => regenerate()}
          class="w-full rounded-md px-3 lg:w-auto py-1{isLoading
            ? 'disabled cursor-progress bg-zinc-400 text-white'
            : 'hoversermonbg border border-primary bg-white text-primary hover:bg-primary/10'}"
          ><i class="fa-solid fa-repeat mr-2" />Regenerate</button
        >
      {/if}

      <DropdownButton
        on:click={() => handleDownload()}
        on:optionClick={option => handleDownload(option.detail.value)}
        disabled={isLoading}
        options={downloadOptions}
      >
        <span class="flex items-center gap-x-2">
          <i class="fa-solid fa-download" />Download
        </span>
      </DropdownButton>
    </div>
  </div>
  <div class="h-[calc(100%-4rem)] w-full overflow-auto p-4">
    {#if isLoading}
      <div class="flex h-full w-full justify-center">
        <div class="flex flex-col items-center gap-4 pt-10">
          <div
            class="spinner h-8 w-8 !border-zinc-200 !border-t-rose-500 dark:!border-zinc-700 dark:!border-t-rose-500"
          />
          <h1 class="text-3xl font-bold text-zinc-400 dark:text-zinc-400">Loading Devotionals...</h1>
        </div>
      </div>
    {:else if devotionals}
      <div class="flex h-full w-full justify-center">
        <div class="h-full w-full max-w-[40rem]">
          {#each devotionals as devotional, i}
            <div class="mb-16 block text-justify">
              <h2 class="flex gap-2 pb-5 text-lg font-bold lg:text-3xl">
                {#if devotional.title.replace(/<[^>]+>/g, '').startsWith('Day')}
                  {@html devotional.title}
                {:else}
                  {@html `Day ${i + 1}: ${devotional.title}`}
                {/if}
              </h2>
              <div class="pl-3">
                <p class="block pb-4">
                  <b class="title block pb-2">Devotional</b>{@html devotional.devotional || devotional.content}
                </p>
                <p class="block pb-4">
                  <b class="title">Bible Verse</b>{@html devotional.verse}
                </p>
                <!-- <p class="block pb-3">Prayer <br/>{devotional.prayer}</p> -->
                <p class="block pb-4">
                  <b class="title">Reflection Question</b>
                  {@html devotional.question}
                </p>
                {#if devotional.quote}
                  <p class="block pb-4">
                    <b class="title">Quote</b>
                    {@html devotional.quote}
                  </p>
                {/if}
                {#if devotional.prayer}
                  <p class="block pb-4">
                    <b class="title">Prayer</b>
                    {@html devotional.prayer}
                  </p>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>
    {:else}
      <div class="flex h-full w-full items-center justify-center">
        <div class="flex flex-col items-center justify-center gap-4">
          <h1 class="pt-10 text-3xl font-bold text-zinc-400 dark:text-zinc-400">No Devotionals Found</h1>
          <p class="text-zinc-400 dark:text-zinc-400">{warningMessage}</p>
        </div>
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .title {
    @apply pr-2 font-bold text-zinc-500;
  }
</style>
